// ─────────────────────────────────────────────────────────────────────────────
// PaymentService — platform-agnostic payment abstraction
//
// Platform routing:
//   DEV mode          → simulates purchase instantly (no real charge)
//   Web (production)  → Stripe Payment Link (VITE_STRIPE_PAYMENT_LINK env var)
//   Steam             → Steamworks handles purchase externally; call grantUnlock()
//                       from your Steamworks overlay callback
//   Mobile (Capacitor)→ RevenueCat SDK; call grantUnlock() on purchase success
//   Windows Store     → MS Store API; call grantUnlock() on purchase success
//
// Stripe flow (web):
//   1. User clicks Buy → game opens Stripe Payment Link in new tab
//   2. User pays → Stripe redirects to game URL with ?unlock=VITE_UNLOCK_TOKEN
//   3. On next game load, handleUrlCallback() verifies token → grantUnlock()
// ─────────────────────────────────────────────────────────────────────────────

const UNLOCK_KEY   = 'pazurumon_unlocked_v1';
const PROGRESS_KEY = 'pazurumon_progress_v3';

export const PaymentService = {
  PRICE_DISPLAY: '€4.99',

  // ── State ──────────────────────────────────────────────────────────────────

  isUnlocked() {
    return localStorage.getItem(UNLOCK_KEY) === 'true';
  },

  /**
   * Grant full-game unlock. Call this after any successful payment,
   * regardless of platform. Safe to call multiple times.
   */
  grantUnlock() {
    localStorage.setItem(UNLOCK_KEY, 'true');

    // Also enable Infinite Mode immediately as part of the unlock reward
    try {
      const raw = localStorage.getItem(PROGRESS_KEY);
      if (raw) {
        const p = JSON.parse(raw);
        p.infiniteModeUnlocked = true;
        localStorage.setItem(PROGRESS_KEY, JSON.stringify(p));
      }
    } catch { /* progress not initialised yet — LevelStore will pick it up */ }
  },

  // ── Purchase flow ──────────────────────────────────────────────────────────

  /**
   * Initiate a purchase. Returns { success, pending, error }.
   * In DEV: resolves after 1.5s with { success: true }.
   * In PROD web: opens Stripe link → returns { success: false, pending: true }.
   */
  async purchase() {
    if (import.meta.env.DEV) {
      return new Promise(resolve => {
        setTimeout(() => resolve({ success: true, platform: 'dev_stub' }), 1500);
      });
    }

    // Web production — Stripe Payment Link
    // Set VITE_STRIPE_PAYMENT_LINK in your .env file
    const stripeLink  = import.meta.env.VITE_STRIPE_PAYMENT_LINK;
    const unlockToken = import.meta.env.VITE_UNLOCK_TOKEN;
    if (stripeLink && unlockToken) {
      const successUrl = encodeURIComponent(`${window.location.origin}?unlock=${unlockToken}`);
      window.open(`${stripeLink}?success_url=${successUrl}`, '_blank');
      return { success: false, pending: true };
    }

    // Steam / Windows Store: purchase is triggered externally.
    // Call PaymentService.grantUnlock() from your native SDK callback.

    return { success: false, error: 'no_provider', message: 'Payment provider not configured.' };
  },

  /**
   * Call once on game startup to handle redirect-based payment callbacks.
   * Strips the ?unlock param from the URL after handling it.
   */
  handleUrlCallback() {
    const params       = new URLSearchParams(window.location.search);
    const receivedToken = params.get('unlock');
    const expectedToken = import.meta.env.VITE_UNLOCK_TOKEN;
    if (receivedToken && expectedToken && receivedToken === expectedToken) {
      this.grantUnlock();
      // Clean up the URL so refreshing doesn't re-trigger
      const clean = window.location.pathname + window.location.hash;
      window.history.replaceState({}, '', clean);
      return true; // newly unlocked this session
    }
    return false;
  },

  /** Restore purchases (mobile / Stripe customer portal). */
  restore() {
    // Mobile: RevenueCat.restorePurchases() → call grantUnlock() on success
    // Stripe: direct user to customer portal / support email
    // For now: check current local state
    return this.isUnlocked();
  },

  /** Dev helper — revoke unlock for testing. */
  _devRevoke() {
    localStorage.removeItem(UNLOCK_KEY);
    try {
      const raw = localStorage.getItem(PROGRESS_KEY);
      if (raw) {
        const p = JSON.parse(raw);
        p.infiniteModeUnlocked = false;
        localStorage.setItem(PROGRESS_KEY, JSON.stringify(p));
      }
    } catch {}
  },
};
